import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';

import {
  InputBase,
  InputProps,
  Typography,
  TypographyProps,
  styled,
} from '@material-ui/core';

type Item = {
  value: string;
  label?: string;
};

interface AutocompleteContext<TItem extends Item = Item> {
  getInputProps: () => Record<string, unknown>;
  getListboxProps: () => Record<string, unknown>;
  getOptionProps: (index: number) => Record<string, unknown>;
  multiple: boolean;
  items: TItem[];
  onClose: () => void;
}

// Context
const CONTEXT = createContext<AutocompleteContext | null>(null);

const useAutocompleteContext = () => {
  const context = useContext(CONTEXT);
  if (!context) {
    throw new Error(
      'Autocomplete compound components must be used within an Autocomplete.Root component',
    );
  }
  return context;
};

// Styled Components
const StyledInput = styled(InputBase)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

// Components

const Input = (props: InputProps) => {
  const { getInputProps } = useAutocompleteContext();
  return <StyledInput {...getInputProps()} {...props} />;
};

const StyledEmpty = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1.5),
}));

export type EmptyProps = TypographyProps & {
  disableTypography?: boolean;
};

const Empty = ({ ...props }: EmptyProps) => {
  const { items } = useAutocompleteContext();
  const { disableTypography, children, ...rest } = props;

  if (items.length > 0) {
    return null;
  }

  if (disableTypography) {
    return <>{children || 'No results'}</>;
  }

  return <StyledEmpty {...rest}>{children || 'No results'}</StyledEmpty>;
};

interface RootProps<TOption extends Item, Multiple extends boolean = false> {
  children: (options: AutocompleteContext<TOption>) => ReactNode;
  items: TOption[];
  multiple?: Multiple;
  value?: Multiple extends true ? string[] : string | null;
  onValueChange?: (
    value: Multiple extends true ? string[] : string | null,
  ) => void;
  onClose?: () => void;

  onInputChange?: (value: string, e?: ChangeEvent<HTMLInputElement>) => void;
  // Return true to prevent default behavior.
  onSelect?: (
    value: string,
    e: MouseEvent | KeyboardEvent,
  ) => boolean | undefined | void;
  disableFilter?: boolean;
}

const Root = <TOption extends Item, Multiple extends boolean = false>({
  children,
  value,
  onValueChange,
  onInputChange,
  items: initialItems,
  multiple = false as Multiple,
  onClose,
  onSelect,
  disableFilter = false,
}: RootProps<TOption, Multiple>) => {
  const [inputValue, setInputValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const selectedValues = useMemo(() => {
    if (multiple) {
      return (value as string[]) || [];
    }
    return value ? [value as string] : [];
  }, [value, multiple]);

  const items = useMemo(() => {
    if (disableFilter) {
      return initialItems;
    }

    return initialItems.filter(option => {
      const searchText = (option.label || option.value).toLowerCase();
      return searchText.includes(inputValue.toLowerCase());
    });
  }, [initialItems, inputValue, disableFilter]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onInputChange?.(event.target.value, event);
    if (event.isDefaultPrevented()) {
      return;
    }

    setInputValue(event.target.value);
    setHighlightedIndex(-1);
  };

  const handleOptionSelect = (
    optionValue: string,
    e: MouseEvent | KeyboardEvent,
  ) => {
    setInputValue('');
    onInputChange?.('');

    if (onSelect) {
      const preventDefault = onSelect(optionValue, e);
      if (preventDefault) {
        return;
      }
    }

    // TypeScript cannot narrow the conditional type `Multiple extends true ? string[] : string | null`
    // inside the function body, so we cast through the resolved union type.
    type Value = Multiple extends true ? string[] : string | null;
    const emit = onValueChange as
      | ((value: string | string[] | null) => void)
      | undefined;

    if (multiple) {
      if (!e.shiftKey) {
        emit?.([optionValue] as Value);
      } else {
        const newValue = selectedValues.includes(optionValue)
          ? selectedValues.filter(v => v !== optionValue)
          : [...selectedValues, optionValue];
        emit?.(newValue as Value);
      }
    } else {
      emit?.(optionValue as Value);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex(prev =>
          prev < items.length - 1 ? prev + 1 : prev,
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        event.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < items.length) {
          handleOptionSelect(items[highlightedIndex].value, event);
        }
        break;
      case 'Escape':
        onClose?.();
        break;
      default:
        break;
    }
  };

  const contextValue = useMemo(
    () => ({
      getInputProps: () => ({
        value: inputValue,
        onChange: handleInputChange,
        onKeyDown: handleKeyDown,
        'data-testid': 'simple-autocomplete-input',
      }),
      getListboxProps: () => ({
        role: 'listbox',
        'aria-multiselectable': multiple,
        'data-testid': 'simple-autocomplete-listbox',
      }),
      getOptionProps: (index: number) => ({
        role: 'option',
        selected:
          selectedValues.includes(items[index].value) ||
          index === highlightedIndex,
        onClick: (e: MouseEvent) => {
          if (e.isDefaultPrevented()) {
            return;
          }

          handleOptionSelect(items[index].value, e);
        },
        'aria-selected': selectedValues.includes(items[index].value),
        'data-highlighted': index === highlightedIndex,
      }),
      onClose: () => onClose?.(),
      items,
      multiple,
    }),
    [
      inputValue,
      selectedValues,
      multiple,
      items,
      highlightedIndex,
      handleOptionSelect,
      handleInputChange,
      handleKeyDown,
      onClose,
    ],
  );

  return (
    <CONTEXT.Provider value={contextValue}>
      {children(contextValue)}
    </CONTEXT.Provider>
  );
};

// Export
export const SimpleAutocomplete = {
  Root,
  Input,
  Empty,
};
